import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrands } from '../contexts/BrandContext';

const BrandDetail = ({ brandSlug }) => {
  const navigate = useNavigate();
  const { getBrandBySlug, updateBrand, deleteBrand } = useBrands();
  const [isEditing, setIsEditing] = useState(false);
  const [editedBrand, setEditedBrand] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', price: '' });

  const brand = getBrandBySlug(brandSlug);

  React.useEffect(() => {
    if (brand) {
      setEditedBrand({ ...brand });
    }
  }, [brand]);

  const handleBack = () => {
    navigate('/');
  };

  const handleEditBrand = () => {
    setIsEditing(true);
  };

  const handleSaveBrand = () => {
    updateBrand(editedBrand);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedBrand({ ...brand });
    setIsEditing(false);
  };

  const handleDeleteBrand = () => {
    if (window.confirm(`Are you sure you want to delete "${brand.name}"?`)) {
      deleteBrand(brand.id);
      navigate('/');
    }
  };

  const handleAddProduct = () => {
    if (newProduct.name && newProduct.price) {
      const updatedBrand = {
        ...editedBrand,
        products: [...editedBrand.products, newProduct]
      };
      setEditedBrand(updatedBrand);
      setNewProduct({ name: '', price: '' });
      setShowAddProduct(false);
    }
  };

  const handleDeleteProduct = (index) => {
    const updatedBrand = {
      ...editedBrand,
      products: editedBrand.products.filter((_, i) => i !== index)
    };
    setEditedBrand(updatedBrand);
  };

  if (!brand) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#F5EFE6'}}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{color: '#3E3E3E'}}>Brand not found</h2>
          <button
            onClick={handleBack}
            className="px-4 py-2 rounded-full font-medium transition-all duration-300"
            style={{backgroundColor: '#C8A96A', color: '#FFFFFF'}}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F5EFE6'}}>
      {/* Header */}
      <div className="relative">
        {/* Hero Image */}
        <div className="h-96 relative overflow-hidden">
          <img
            src={brand.image}
            alt={brand.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="absolute top-8 left-8 p-3 rounded-full bg-white/90 hover:bg-white transition-colors shadow-lg"
          >
            <span className="material-symbols-outlined" style={{color: '#C8A96A'}}>
              arrow_back
            </span>
          </button>

          {/* Brand Actions */}
          <div className="absolute top-8 right-8 flex gap-2">
            {!isEditing ? (
              <>
                <button
                  onClick={handleEditBrand}
                  className="p-3 rounded-full bg-white/90 hover:bg-white transition-colors shadow-lg"
                >
                  <span className="material-symbols-outlined" style={{color: '#C8A96A'}}>
                    edit
                  </span>
                </button>
                <button
                  onClick={handleDeleteBrand}
                  className="p-3 rounded-full bg-red-500/90 hover:bg-red-500 transition-colors shadow-lg"
                >
                  <span className="material-symbols-outlined text-white">
                    delete
                  </span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSaveBrand}
                  className="p-3 rounded-full bg-green-500/90 hover:bg-green-500 transition-colors shadow-lg"
                >
                  <span className="material-symbols-outlined text-white">
                    save
                  </span>
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="p-3 rounded-full bg-stone-500/90 hover:bg-stone-500 transition-colors shadow-lg"
                >
                  <span className="material-symbols-outlined text-white">
                    close
                  </span>
                </button>
              </>
            )}
          </div>

          {/* Brand Name */}
          <div className="absolute bottom-8 left-8">
            {isEditing ? (
              <input
                type="text"
                value={editedBrand.name}
                onChange={(e) => setEditedBrand({...editedBrand, name: e.target.value})}
                className="text-6xl font-bold bg-transparent border-b-2 border-white text-white placeholder-white/70 focus:outline-none"
                style={{color: '#FFFFFF'}}
              />
            ) : (
              <h1 className="text-6xl font-bold" style={{color: '#FFFFFF'}}>
                {brand.name}
              </h1>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Brand Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{color: '#3E3E3E'}}>About</h2>
              {isEditing ? (
                <textarea
                  value={editedBrand.description}
                  onChange={(e) => setEditedBrand({...editedBrand, description: e.target.value})}
                  className="w-full p-4 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={4}
                  style={{focusRingColor: '#C8A96A'}}
                />
              ) : (
                <p className="text-stone-600 leading-relaxed">
                  {brand.description}
                </p>
              )}
            </div>

            {/* Features */}
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{color: '#3E3E3E'}}>Features</h2>
              <div className="grid grid-cols-2 gap-4">
                {isEditing ? (
                  editedBrand.features.map((feature, index) => (
                    <input
                      key={index}
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const updatedFeatures = [...editedBrand.features];
                        updatedFeatures[index] = e.target.value;
                        setEditedBrand({...editedBrand, features: updatedFeatures});
                      }}
                      className="p-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      style={{focusRingColor: '#C8A96A'}}
                    />
                  ))
                ) : (
                  brand.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-sm" style={{color: '#C8A96A'}}>
                        check_circle
                      </span>
                      <span>{feature}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Products */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold" style={{color: '#3E3E3E'}}>Products</h2>
              {(isEditing || showAddProduct) && (
                <button
                  onClick={() => setShowAddProduct(!showAddProduct)}
                  className="p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
                  style={{color: '#C8A96A'}}
                >
                  <span className="material-symbols-outlined">add</span>
                </button>
              )}
            </div>

            {/* Add Product Form */}
            {showAddProduct && (
              <div className="mb-4 p-4 bg-white rounded-lg shadow-sm">
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Product name"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    className="w-full p-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    style={{focusRingColor: '#C8A96A'}}
                  />
                  <input
                    type="text"
                    placeholder="Price"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                    className="w-full p-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    style={{focusRingColor: '#C8A96A'}}
                  />
                  <button
                    onClick={handleAddProduct}
                    className="w-full px-4 py-2 rounded-full font-medium transition-all duration-300"
                    style={{backgroundColor: '#C8A96A', color: '#FFFFFF'}}
                  >
                    Add Product
                  </button>
                </div>
              </div>
            )}

            {/* Product List */}
            <div className="space-y-3">
              {(isEditing ? editedBrand : brand).products.map((product, index) => (
                <div key={index} className="flex justify-between items-center p-4 bg-white rounded-lg shadow-sm">
                  <div>
                    <h3 className="font-semibold" style={{color: '#3E3E3E'}}>{product.name}</h3>
                    <p style={{color: '#C8A96A'}}>{product.price}</p>
                  </div>
                  {isEditing && (
                    <button
                      onClick={() => handleDeleteProduct(index)}
                      className="p-2 rounded-full bg-red-100 hover:bg-red-200 transition-colors"
                    >
                      <span className="material-symbols-outlined text-red-600 text-sm">delete</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandDetail;
